import React from "react";
import { Formik, Form } from "formik";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FormField from "../utility/FormField";
import Button from "./Button";
import { toast } from "react-toastify";
import { changePassword } from "./api/changePassword";
import { resetPassword } from "../store/reducers/resetpassword";
import { createPassword } from "../store/reducers/createPassword";
import background from "../assets/background.png";
import Logo from "../assets/sbw 1.png";

const CreatePassword = ({ mode = "page", setShowUserPasswordModal = null }) => {
  const dispatch = useDispatch();
  const { role, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = React.useState(false);

  const validationSchema = Yup.object().shape({
    sm_password: Yup.string().required("Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("sm_password"), null], "Passwords are not matching")
      .required("Re-enter password is required"),
  });

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let response;

      if (mode === "modal") {
        response = await dispatch(changePassword(values));
        if (response.payload?.status === "success") {
          toast.success(response.payload.message);
          setShowUserPasswordModal(false);
        }
      } else {
        let action;

        if (location.pathname === `/reset-password/${id}`) {
          action = resetPassword({ id, values });
        } else {
          action = createPassword({ id, values, type: role });
        }
        response = await dispatch(action);
        if (response?.payload?.status === "success") {
          navigate("/password-confirmation", {
            state: location.pathname.includes("reset-password")
              ? "/reset-password"
              : `/signup/${role}`,
          });
        }
      }
    } catch (error) {
      toast.error(error.response.data.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderForm = () => (
    <Formik
      initialValues={{
        sm_password: "",
        confirm_password: "",
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, isValid, dirty }) => (
        <Form className="w-full">
          <div className="space-y-4 w-full">
            <FormField
              {...(mode === "modal"
                ? { className: "w-full text-black" }
                : { className: "w-full text-white" })}
              label="Password"
              name="sm_password"
              as="password"
              showPassword={showPassword}
              togglePasswordVisibility={togglePasswordVisibility}
            />
            <FormField
              {...(mode === "modal"
                ? { className: "w-full text-black" }
                : { className: "w-full text-white" })}
              label="Re-enter password"
              name="confirm_password"
              as="password"
              showPassword={showPassword}
              togglePasswordVisibility={togglePasswordVisibility}
            />
          </div>

          <div
            className={`flex mt-6 gap-4 ${mode === "modal" ? "" : "flex-col w-full"}`}
          >
            {mode === "modal" ? (
              <>
                <Button
                  type="button"
                  onClick={() => setShowUserPasswordModal(false)}
                  className="w-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !isValid || !dirty}
                  primary={isValid && dirty}
                  className="w-full"
                >
                  Submit
                </Button>
              </>
            ) : (
              <div className="mt-8 w-full">
                <Button
                  type="submit"
                  disabled={isSubmitting || !isValid || !dirty}
                  primary={isValid && dirty}
                  className="w-full"
                >
                  Confirm password
                </Button>
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );

  if (mode === "modal") {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 w-full max-w-md mx-auto shadow-lg relative">
          <button
            className="absolute top-2 right-2 text-gray-500 hover:text-black text-2xl font-bold"
            onClick={() => setShowUserPasswordModal(false)}
          >
            &times;
          </button>
          <h2 className="text-2xl font-semibold mb-6 text-center">
            Change Password
          </h2>
          {renderForm()}
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="grid grid-cols-12 font-poppins">
        <div className="col-span-4 md:col-span-6 lg:col-span-7 xl:col-span-7 2xl:col-span-8"></div>
        <div className="min-h-screen p-8 col-span-8 md:col-span-6 lg:col-span-5 xl:col-span-5 2xl:col-span-4 bg-[#0d223d]/60 backdrop-blur-[80px] shadow-lg inline-flex">
          <div className="w-full flex-col justify-center items-center inline-flex gap-6">
            <img src={Logo} alt="Logo" />
            <div className="w-full border-t border-[#b3b5bd] my-4"></div>
            <h6 className="text-center text-[#F3F4F8] text-xs md:text-sm lg:text-base mb-6">
              {location.pathname === `/reset-password/${id}`
                ? `Create a new password for the account linked with ${localStorage.getItem("email")}`
                : "Your details have been verified and now you can create your login password."}
            </h6>
            {renderForm()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePassword;
